import { assert } from 'cadenza/utils/custom-error';
import Feature from 'ol/Feature';
import type OlMap from 'ol/Map';
import Point from 'ol/geom/Point';
import { ARROW_MINIMUM_VISIBILITY_DISTANCE_SQUARED, createArrowStyle } from 'cadenza/map/timeline-visualization/timeline-constants';
import { ArrowCachingProxyStyle } from 'cadenza/map/timeline-visualization/styles/arrow-caching-proxy-style';
import { calculateRotation } from 'cadenza/map/style/style-util';
import type { CoordinateDto } from 'cadenza/api-client/dto/coordinate-dto';
import {
  type TimelineDto,
  TimelineLayerFadingDurationUnit,
  type TimelineLayerFadingSettingsDurationDto,
  TimelineLayerFadingTransition,
  type TimelinePointDto
} from 'cadenza/api-client/timeline-dto';

export interface Timeline {
  /** fading behavior configuration */
  fading: object;
  /** cache for fading-related data */
  fadingFactorCache: Map<string, object>;
  /** if true, the last displayed point of the timeline is marked with a crosshair */
  highlightLastPoint?: boolean;
  /** ID of the map layer that the timeline belongs to, e.g. "Weistrche~44" */
  layerId: string;
  /** human-readable name of the map layer that the timeline belongs to, e.g. "Weißstörche" */
  layerName: string;
  /** if true, lines are drawn to connect the points of the timeline */
  linesVisible: boolean;
  /** number of points that can be displayed at the same time */
  maxDisplayPointCount: number;
  /**  maps from timeline point index to the corresponding map layer feature index */
  pointIndexToFeatureIndexMap: Map<number, number>;
  /** points of the timeline, with time, coordinates and more */
  points: TimelinePoint[];
  /** color as string, e.g. "rgba(0, 0, 255, 1)" */
  timelineColor: string;
  /** ID that uniquely identifies the timeline within its map layer, e.g. "Dana" */
  timelineId: string;
  /** ID that uniquely identifies the timeline within the whole map, e.g. "Weistrche~44:timeline:Dana" */
  uniqueId: string;
}

export interface TimelinePoint {
  coordinate: CoordinateDto;
  /** function for calculating the fading behavior */
  fadingSupplier: FadingSupplier;
  /** fading type and current fading degree */
  result: FadingResult;
  /** visual style of point */
  style: object;
}

type FadingSupplier = (previousPoint: TimelinePoint, endPoint: TimelinePoint) => { fadingFactor: number; fadingTransition: TimelineLayerFadingTransition };

export interface FadingResult {
  /** number from 0 to one indicating how much the point has already faded */
  fadingFactor: number;
  fadingTransition: TimelineLayerFadingTransition;
}

const DEFAULT_FADING = { fadingFactor: 1, fadingTransition: TimelineLayerFadingTransition.NONE };

export function toUnitFromDuration (duration: TimelineLayerFadingSettingsDurationDto) {
  return toUnit(duration.milliseconds, duration.configurationUnit);
}

export function toUnit (milliseconds: number, durationUnit: TimelineLayerFadingDurationUnit) {
  let result = milliseconds;
  /* eslint-disable no-fallthrough */
  switch (durationUnit) {
    case TimelineLayerFadingDurationUnit.DAYS:
      result /= 24;
    case TimelineLayerFadingDurationUnit.HOURS:
      result /= 60;
    case TimelineLayerFadingDurationUnit.MINUTES:
      result /= 60;
    case TimelineLayerFadingDurationUnit.SECONDS:
      result /= 1000;
    case TimelineLayerFadingDurationUnit.MILLISECONDS:
      return result;
  }
  throw new Error(`Unknown duration unit '${durationUnit}'`);
}

export function toDuration (value: number, durationUnit: TimelineLayerFadingDurationUnit) {
  return {
    milliseconds: toMilliseconds(value, durationUnit),
    configurationUnit: durationUnit
  };
}

export function toMilliseconds (value: number, durationUnit: TimelineLayerFadingDurationUnit) {
  let result = value;
  switch (durationUnit) {
    case TimelineLayerFadingDurationUnit.DAYS:
      result *= 24;
    case TimelineLayerFadingDurationUnit.HOURS:
      result *= 60;
    case TimelineLayerFadingDurationUnit.MINUTES:
      result *= 60;
    case TimelineLayerFadingDurationUnit.SECONDS:
      result *= 1000;
    case TimelineLayerFadingDurationUnit.MILLISECONDS:
      return result;
  }
  throw new Error(`Unknown duration unit '${durationUnit}'`);
}

export function sortPointsWithTimelinesByTime (points: { point: { time: number; uniqueTimelineId: string } }[]) {
  points.sort((firstPointWithTimeline, secondPointWithTimeline) => {
    assert(firstPointWithTimeline.point.time != null, 'Timeline points must have a time');
    assert(secondPointWithTimeline.point.time != null, 'Timeline points must have a time');

    if (firstPointWithTimeline.point.time < secondPointWithTimeline.point.time) {
      return -1;
    } else if (firstPointWithTimeline.point.time === secondPointWithTimeline.point.time) {
      return 0;
    } else {
      return 1;
    }
  });
}

export function getTimespan (timelines: TimelineDto[]) {
  let min = Number.MAX_VALUE;
  let max = Number.MIN_VALUE;
  timelines.forEach((timeline) => {
    const timelineMin = timeline.points[0].time;
    const timelineMax = timeline.points[timeline.points.length - 1].time;
    if (timelineMin < min) {
      min = timelineMin;
    }
    if (timelineMax > max) {
      max = timelineMax;
    }
  });
  return { min, max };
}

export function getSiblingsInTimePoints (points: TimelinePointDto[], i: number) {
  const previous = points[i - 1];
  if (previous) {
    const currentTime = points[i].time;
    if (previous.time === currentTime) {
      return [ previous ];
    }
  }
  return [];
}

/**
 * Gets the nearest points in time immediately before the current one. If there are several siblings right before the current point,
 * returns all of them, otherwise it just returns an array of 1, the first predeccesor.
 *
 * @param points - Array of points
 * @param pointIndex - the index in the ordered array
 * @return the array of nearest predecessors, there can be more than 1 if there are siblings
 */
export function getImmediatelyPreviousInTimePoints (points: TimelinePointDto[], pointIndex: number): TimelinePointDto[] {
  const currentTime = points[pointIndex].time;

  const predecessors = [];
  let previousTime = null;
  for (let j = pointIndex - 1; j >= 0; j--) {
    const previous = points[j];
    const jTime = previous.time;
    if (previousTime === null) {
      if (jTime < currentTime) {
        previousTime = jTime;
      }
    }

    if (previousTime !== null) {
      if (jTime < previousTime) {
        break;
      }

      predecessors.push(previous);
    }
  }
  return predecessors;
}

/**
 * Returns all the points of all timelines in the specified time range.
 *
 * @param timelines - the timelines to search in
 * @param minTime - lower limit of time range in ms (inclusive)
 * @param maxTime - upper limit of time range in ms (inclusive)
 * @return Array of points in the specified time range
 */
export function getAllPointsInTimeRange (timelines: TimelineDto[], minTime: number, maxTime: number): TimelinePointDto[] {
  const result: TimelinePointDto[] = [];

  timelines.forEach((timeline) => {
    // possible Optimization: search for starting point with binary search instead of searching seqentially
    let i = 0;
    let currentTime = minTime;
    while (i < timeline.points.length && currentTime <= maxTime) {
      const nextPoint = timeline.points[i];
      if (nextPoint && nextPoint.time >= minTime && nextPoint.time <= maxTime) {
        result.push(nextPoint);
      }
      currentTime = nextPoint.time;
      i++;
    }
  });

  return result;
}

/**
 * Returns the last rendered point for the specified time, with the timeline it belongs to.
 *
 * @param timelines - the timelines to search in
 * @param time - the time
 * @return point, or null if no next point is found
 */
export function getEndingRenderedPoint (timelines: TimelineDto[], time: number): TimelinePointDto | null {
  const endingRenderedPoints: TimelinePointDto[] = [];
  timelines.forEach((timeline) => {
    const currentIndex = getFirstPointIndexOnOrBeforeTime(timeline, time);
    if (currentIndex !== -1) {
      endingRenderedPoints.push(timeline.points[currentIndex]);
    }
  });
  if (endingRenderedPoints.length > 0) {
    sortPointsByTime(endingRenderedPoints);
    return endingRenderedPoints[endingRenderedPoints.length - 1];
  } else {
    return null;
  }
}

/**
 * Returns the point that comes after the specified time. If two timelines have a point at exactly
 * the same time, it is random which one is returned.
 *
 * @param timelines - the timelines to search in
 * @param time - time in ms
 * @return point, or null if no next point is found
 */
export function getNextPoint (timelines: TimelineDto[], time: number): TimelinePointDto | null {
  const nextPointsFromAllTimelines: TimelinePointDto[] = [];
  timelines.forEach((timeline) => {
    const currentIndex = getFirstPointIndexOnOrBeforeTime(timeline, time);
    if (currentIndex < timeline.points.length - 1) {
      nextPointsFromAllTimelines.push(timeline.points[currentIndex + 1]);
    }
  });

  if (nextPointsFromAllTimelines.length === 0) {
    return null;
  } else {
    sortPointsByTime(nextPointsFromAllTimelines);
    return nextPointsFromAllTimelines[0];
  }
}

/**
 * Returns the point that comes before the speficied time. If two timelines have a point at exactly
 * the same time, it is random which one is returned.
 *
 * @param timelines - the timelines to search in
 * @param time - time in ms
 * @return point, or null if no previous point is found
 */
export function getPrevPoint (timelines: TimelineDto[], time: number) {
  const prevPointsFromAllRelevantTimelines: TimelinePointDto[] = [];
  timelines.forEach((timeline) => {
    const endingIndex = getFirstPointIndexOnOrBeforeTime(timeline, time);
    if (endingIndex !== -1) {
      const endingPoint = timeline.points[endingIndex];
      if (endingPoint.time < time) {
        prevPointsFromAllRelevantTimelines.push(endingPoint);
      } else if (endingIndex > 0) {
        prevPointsFromAllRelevantTimelines.push(timeline.points[endingIndex - 1]);
      }
    }
  });

  if (prevPointsFromAllRelevantTimelines.length === 0) {
    return null;
  } else {
    sortPointsByTime(prevPointsFromAllRelevantTimelines);
    return prevPointsFromAllRelevantTimelines[prevPointsFromAllRelevantTimelines.length - 1];
  }
}

export function sortPointsByTime (points: TimelinePointDto[]) {
  points.sort((firstPoint, secondPoint) => {
    assert(firstPoint.time != null, 'Timeline points must have a time');
    assert(secondPoint.time != null, 'Timeline points must have a time');

    if (firstPoint.time < secondPoint.time) {
      return -1;
    } else if (firstPoint.time === secondPoint.time) {
      return 0;
    } else {
      return 1;
    }
  });
}

// possible optimization: binary search
function getFirstPointIndexOnOrBeforeTime (timeline: TimelineDto, time: number) {
  let i = timeline.points.length - 1;
  while (i > 0 && timeline.points[i].time > time) {
    i--;
  }
  return timeline.points[i].time <= time ? i : -1;
}

export function createArrowsToPreviousCoordinates (
  previousPoints: TimelinePoint[],
  endPoint: TimelinePoint,
  imageCache: Map<string, object>,
  map: OlMap,
  cachedZoom: object,
  fadingSupplier: FadingSupplier) {
  const arrowFeatures = [];
  for (let i = 0; i < previousPoints.length; i++) {
    if (!coordinatesEqual(previousPoints[i].coordinate, endPoint.coordinate)) {
      arrowFeatures.push(createArrowToPreviousCoordinate(previousPoints[i], endPoint, imageCache, map, cachedZoom, fadingSupplier));
    }
  }
  return arrowFeatures;
}

export function createArrowToPreviousCoordinate (
  previousPoint: TimelinePoint,
  endPoint: TimelinePoint,
  imageCache: Map<string, object>,
  map: OlMap,
  cachedZoom: object,
  fadingSupplier: FadingSupplier = () => DEFAULT_FADING) {
  const previousCoordinate = previousPoint.coordinate;
  const coordinate = endPoint.coordinate;
  const arrow = new Feature({
    geometry: new Point(calculateCenter(previousCoordinate, coordinate))
  });
  const rotation = calculateRotation(previousCoordinate, coordinate);
  const rotatedStyle = createArrowStyle();
  rotatedStyle.getImage().setRotation(rotation);
  arrow.setStyle(new ArrowCachingProxyStyle(
    rotatedStyle,
    fadingSupplier(previousPoint, endPoint),
    imageCache,
    rotation,
    calculateDistanceSquared(previousCoordinate, coordinate),
    () => isArrowVisible(map, previousCoordinate, coordinate),
    cachedZoom));

  return arrow;
}

function calculateDistanceSquared (c1: CoordinateDto, c2: CoordinateDto) {
  return c1[0] * c2[0] + c1[1] * c2[1];
}

function coordinatesEqual (c1: CoordinateDto, c2: CoordinateDto) {
  return c1[0] === c2[0] && c1[1] === c2[1];
}

function isArrowVisible (
  map: OlMap,
  startCoordinate: CoordinateDto,
  endCoordinate: CoordinateDto) {
  const pixelCoordinate = map.getPixelFromCoordinate(endCoordinate);
  const previousPixelFromCoordinate = map.getPixelFromCoordinate(startCoordinate);
  const dx = pixelCoordinate[0] - previousPixelFromCoordinate[0];
  const dy = pixelCoordinate[1] - previousPixelFromCoordinate[1];
  const distanceSquared = dx * dx + dy * dy;
  return distanceSquared > ARROW_MINIMUM_VISIBILITY_DISTANCE_SQUARED;
}

function calculateCenter (coordinate1: CoordinateDto, coordinate2: CoordinateDto): CoordinateDto {
  return [
    (coordinate1[0] + coordinate2[0]) / 2,
    (coordinate1[1] + coordinate2[1]) / 2 ];
}
